import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ToggleButton } from '@mui/material';
import type { Editor } from '@tiptap/core';
import { FormatSize } from '@mui/icons-material';
import { useState } from 'react';

interface WrapperProps {
  children?: React.ReactNode;
}

const LargeHeader = ({ children }: WrapperProps) => <h1>{children}</h1>;
const NormalHeader = ({ children }: WrapperProps) => <h2>{children}</h2>;
const Subheader = ({ children }: WrapperProps) => <h3>{children}</h3>;
const NormalText = ({ children }: WrapperProps) => <p>{children}</p>;

const formattingOptions = [
  { text: 'Large header', headingLevel: 1 as const, Component: LargeHeader },
  { text: 'Normal header', headingLevel: 2 as const, Component: NormalHeader },
  { text: 'Subheader', headingLevel: 3 as const, Component: Subheader },
  { text: 'Normal text', headingLevel: 0 as const, Component: NormalText }, // we only use 3 levels, force 0 to be paragraph
];

interface Props {
  editor: Editor;
}

export const FormatSizeButton = ({ editor }: Props) => {
  const [headingAnchorEl, setHeadingAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const headingOpen = Boolean(headingAnchorEl);

  const handleHeadingClick = (event: React.MouseEvent<HTMLElement>) => {
    setHeadingAnchorEl(event.currentTarget);
  };

  const handleHeadingClose = () => {
    setHeadingAnchorEl(null);
  };

  return (
    <>
      <ToggleButton
        id="text-size-button"
        value="textSize"
        aria-controls={headingOpen ? 'text-size-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={headingOpen ? 'true' : undefined}
        selected={editor.isActive('orderedList')}
        onClick={handleHeadingClick}
      >
        <FormatSize />
      </ToggleButton>
      <Menu
        id="text-size-menu"
        anchorEl={headingAnchorEl}
        open={headingOpen}
        onClose={handleHeadingClose}
        MenuListProps={{
          'aria-labelledby': 'text-size-button',
        }}
      >
        {formattingOptions.map(({ text, headingLevel, Component }) => (
          <MenuItem
            key={text}
            selected={
              headingLevel === 0
                ? editor.isActive('paragraph')
                : editor.isActive('heading', { level: headingLevel })
            }
            onClick={() => {
              if (headingLevel === 0) {
                editor?.chain().focus().setParagraph().run();
              } else {
                editor
                  ?.chain()
                  .focus()
                  .toggleHeading({ level: headingLevel })
                  .run();
              }

              handleHeadingClose();
            }}
          >
            <Component>{text}</Component>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
